/**
 * Screen Navigation Hook
 * Provides navigation functions for screen flow
 */

import { useCallback, useMemo } from 'react';
import { useScreenFlow } from './useScreenFlow';
import type { ScreenType } from '../types/screens';
import type { ScreenDataMap } from '../types/screenData';
import { logger } from '../utils/logger';
import { sanitizeScreenDataForClient } from '../utils/verificationResultSanitizer';

const routerLogger = logger.clone({ labels: { component: 'ScreenRouter' }});

export function useScreenNavigation() {
  const { actions } = useScreenFlow();

  const navigateToScreen = useCallback(<T extends ScreenType>(
      screen: T,
      data?: ScreenDataMap[T]
    ) => {
      const safeData = data ? sanitizeScreenDataForClient(data) : undefined;
      const result = safeData?.verificationResult;
      routerLogger.debug('Navigating to screen', {
        labels: {
          screen,
          hasData: `${!!safeData}`,
          outcome: result?.outcome || result?.status || 'none',
          failureCategory: result?.failureCategory || 'none',
          sessionId: result?.session_id || result?.sessionId || 'none',
        },
      });
      actions.navigateToScreen(screen, safeData);
    }, [actions]);
  const goBack = useCallback(() => {
    routerLogger.debug('Going back');
    actions.goBack();
  }, [actions]);
  const resetFlow = useCallback(() => {
    routerLogger.debug('Resetting flow');
    actions.resetFlow();
  }, [actions]);

  return useMemo(() => ({
    navigateToScreen,
    goBack,
    resetFlow,
  }), [goBack, navigateToScreen, resetFlow]);
}
